import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import type { AppRole } from "@/lovable-demo/lib/governance/types";
import { fmtDate } from "@/lovable-demo/lib/warranty/calc";
import {
  PFMEA_FIELD_LABEL,
  pfmeaChangePreview,
  requestApChange,
  validatePfmeaChange,
} from "@/lovable-demo/lib/warranty/pfmea-governance";
import { ApBadge } from "@/lovable-demo/components/compliance-verdict";
import {
  requestStatus,
  requiredRoles,
  requiresDirector,
  SEVERITY_DESIGN_NOTE,
  SEVERITY_REDUCTION_NOTICE,
  SIGNOFF_ROLE_LABEL,
  signoffEligibility,
  signoffsFor,
  type GovernanceResult,
  type PfmeaChangeRequest,
  type PfmeaSignoff,
  type SignoffRole,
} from "@/lovable-demo/lib/warranty/pfmea-signoff";
import type { PfmeaAfterField, PfmeaRow } from "@/lovable-demo/lib/warranty/types";

/**
 * After-action rating dropdown. Picking a value never changes the rating: it
 * opens a confirm dialog that submits a change request for dual sign-off. The
 * dropdown is always bound to the approved value (value={row[field]}), so a
 * cancelled, pending or failed request leaves it where it was.
 */
export function PfmeaRatingSelect({
  row,
  field,
  openRequest,
  onConfirm,
}: {
  row: PfmeaRow;
  field: PfmeaAfterField;
  openRequest: PfmeaChangeRequest | undefined;
  onConfirm: (value: number, rationale: string) => Promise<GovernanceResult>;
}) {
  const [pending, setPending] = useState<number | null>(null);
  const [rationale, setRationale] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setPending(null);
    setRationale("");
    setError(null);
  };

  const preview = pending != null ? pfmeaChangePreview(row, field, pending) : null;
  const threeWay =
    pending != null && requiresDirector({ field, oldValue: row[field], newValue: pending });

  const submit = async () => {
    if (pending == null) return;
    const invalid = validatePfmeaChange(row, field, pending, rationale);
    if (invalid) return setError(invalid);
    setBusy(true);
    try {
      const result = await onConfirm(pending, rationale.trim());
      if (result.ok) {
        toast.success("Change submitted for dual sign-off", {
          description: threeWay
            ? "Severity stays unchanged until Quality, Manufacturing Engineering and a Quality Director all approve."
            : "The rating stays unchanged until Quality and Manufacturing Engineering both approve.",
        });
        close();
      } else {
        setError(result.error);
        toast.error("Change request not recorded", { description: result.error });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-1">
      <select
        aria-label={PFMEA_FIELD_LABEL[field]}
        value={row[field]}
        disabled={!!openRequest}
        title={openRequest ? "A change to this rating is pending sign-off" : undefined}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (v !== row[field]) setPending(v);
        }}
        className="num rounded border border-input bg-card px-1.5 py-0.5 text-sm disabled:opacity-60"
      >
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
      {openRequest && (
        <span className="num rounded border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wider text-warning">
          Pending → {openRequest.newValue}
        </span>
      )}
      <Dialog open={pending != null} onOpenChange={(o) => !o && !busy && close()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Propose PFMEA rating change</DialogTitle>
            <DialogDescription>
              {row.failureMode} — {PFMEA_FIELD_LABEL[field]}
            </DialogDescription>
          </DialogHeader>
          {preview && (
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-[0.66rem] uppercase tracking-wider text-muted-foreground">
                  Rating
                </p>
                <p className="num">
                  {preview.oldValue} → <span className="font-semibold">{preview.newValue}</span>
                </p>
              </div>
              <div>
                <p className="text-[0.66rem] uppercase tracking-wider text-muted-foreground">
                  Action Priority
                </p>
                <p className="flex items-center gap-1">
                  <ApBadge ap={preview.apBefore} /> → <ApBadge ap={preview.apAfter} />
                </p>
              </div>
              <div>
                <p className="text-[0.66rem] uppercase tracking-wider text-muted-foreground">
                  RPN (legacy)
                </p>
                <p className="num">
                  {preview.rpnBefore} → <span className="font-semibold">{preview.rpnAfter}</span>{" "}
                  <span className={preview.rpnDelta > 0 ? "text-destructive" : "text-success"}>
                    ({preview.rpnDelta > 0 ? "+" : ""}
                    {preview.rpnDelta})
                  </span>
                </p>
              </div>
            </div>
          )}
          {threeWay && (
            <div className="rounded border border-warning/50 bg-warning/10 p-2 text-xs">
              <p className="font-semibold text-warning">{SEVERITY_REDUCTION_NOTICE}</p>
              <p className="mt-1 text-foreground/90">{SEVERITY_DESIGN_NOTE}</p>
            </div>
          )}
          <p className="rounded border border-border/70 bg-elevated/40 p-2 text-xs text-muted-foreground">
            {threeWay
              ? "This creates a change request. Severity and RPN stay at the approved value until three different people — a Quality approver, a Manufacturing Engineering approver and a Quality Director, none of them you — all approve."
              : "This creates a change request. The rating and RPN stay at the approved value until one Quality approver and one Manufacturing Engineering approver — two different people, not you — both approve."}
          </p>
          <div className="space-y-1.5">
            <label htmlFor={`pf-rat-${row.id}-${field}`} className="text-xs text-muted-foreground">
              Rationale (required)
            </label>
            <Textarea
              id={`pf-rat-${row.id}-${field}`}
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              className="min-h-20 text-xs"
            />
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <DialogFooter>
            <Button variant="outline" size="sm" disabled={busy} onClick={close}>
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={submit}>
              {busy ? "Recording…" : "Submit for sign-off"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const STEP_LABEL: Record<PfmeaSignoff["step"], string> = {
  approve_quality: "Approved — Quality",
  approve_manufacturing: "Approved — Manufacturing Engineering",
  approve_director: "Approved — Quality Director (Severity reduction)",
  reject: "Rejected",
  applied: "Applied",
};

/** Shows which required sign-off slots are done and which are outstanding. */
export function SignoffSlots({
  request,
  signoffs,
}: {
  request: PfmeaChangeRequest;
  signoffs: PfmeaSignoff[];
}) {
  const mine = signoffsFor(request.id, signoffs);
  const roles = requiredRoles(request);
  return (
    <div className="mt-1.5">
      {roles.length === 3 && (
        <p className="text-[0.7rem] font-semibold text-warning">{SEVERITY_REDUCTION_NOTICE}</p>
      )}
      <div className="mt-1 flex flex-wrap gap-1.5">
        {roles.map((r) => {
          const done = mine.find((s) => s.step === `approve_${r}`);
          return (
            <span
              key={r}
              className={
                "rounded border px-1.5 py-0.5 text-[0.64rem] " +
                (done
                  ? "border-success/40 bg-success/10 text-success"
                  : "border-border text-muted-foreground")
              }
            >
              {done ? "✓" : "○"} {SIGNOFF_ROLE_LABEL[r]} —{" "}
              {done ? done.decidedByLabel.split(",")[0] : "outstanding"}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** Approve / Reject controls for one request. Comment drafts are per request. */
export function SignoffActions({
  request,
  signoffs,
  me,
  onSignOff,
}: {
  request: PfmeaChangeRequest;
  signoffs: PfmeaSignoff[];
  me: { userId: string | null; roles: AppRole[] };
  onSignOff: (
    request: PfmeaChangeRequest,
    decision: "approve" | "reject",
    role: SignoffRole,
    comment: string,
  ) => Promise<GovernanceResult>;
}) {
  const [comments, setComments] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const comment = comments[request.id] ?? "";
  const eligible = signoffEligibility({ userId: me.userId, roles: me.roles, request, signoffs });

  if (!eligible.roles.length)
    return <p className="mt-1 text-[0.7rem] italic text-muted-foreground">{eligible.reason}</p>;

  const act = async (decision: "approve" | "reject", role: SignoffRole) => {
    setBusy(true);
    setError(null);
    try {
      const r = await onSignOff(request, decision, role, comment);
      if (r.ok) {
        setComments((c) => ({ ...c, [request.id]: "" }));
        toast.success(
          r.status === "applied"
            ? "Both approvals recorded — rating change applied"
            : decision === "reject"
              ? "Rejection recorded — rating unchanged"
              : `${SIGNOFF_ROLE_LABEL[role]} approval recorded`,
        );
      } else {
        setError(r.error);
        toast.error("Sign-off not recorded", { description: r.error });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-2 space-y-1.5">
      <Textarea
        aria-label="Sign-off comment"
        placeholder="Comment (required)"
        value={comment}
        onChange={(e) => setComments((c) => ({ ...c, [request.id]: e.target.value }))}
        className="min-h-14 text-xs"
      />
      <div className="flex flex-wrap gap-1.5">
        {eligible.roles.map((role) => (
          <span key={role} className="flex gap-1.5">
            <Button size="sm" disabled={busy} onClick={() => act("approve", role)}>
              Approve as {SIGNOFF_ROLE_LABEL[role]}
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => act("reject", role)}>
              Reject as {SIGNOFF_ROLE_LABEL[role]}
            </Button>
          </span>
        ))}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

/** Pending badge, sign-off actions and full sign-off history for one PFMEA row. */
export function PfmeaSignoffHistory({
  row,
  requests,
  signoffs,
  me,
  onSignOff,
}: {
  row: PfmeaRow;
  requests: PfmeaChangeRequest[];
  signoffs: PfmeaSignoff[];
  me: { userId: string | null; roles: AppRole[] };
  onSignOff: Parameters<typeof SignoffActions>[0]["onSignOff"];
}) {
  const mine = requests
    .filter((r) => r.rowId === row.id)
    .slice()
    .reverse();
  const legacy = (row.revisions ?? []).filter((r) => !r.requestId);
  if (!mine.length && !legacy.length) return null;
  return (
    <div className="mt-3 space-y-2">
      <p className="text-[0.66rem] font-semibold uppercase tracking-wider text-muted-foreground">
        Rating sign-off history
      </p>
      {mine.map((req) => {
        const offs = signoffsFor(req.id, signoffs);
        const status = requestStatus(offs);
        return (
          <div key={req.id} className="rounded border border-border/70 bg-card/60 p-2.5 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={
                  "rounded px-1.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wider " +
                  (status === "pending"
                    ? "border border-warning/40 bg-warning/10 text-warning"
                    : status === "applied"
                      ? "border border-success/40 bg-success/10 text-success"
                      : "border border-destructive/40 bg-destructive/10 text-destructive")
                }
              >
                {status === "pending"
                  ? "Pending sign-off"
                  : status === "applied"
                    ? "Applied"
                    : "Rejected"}
              </span>
              <span className="text-foreground">
                {PFMEA_FIELD_LABEL[req.field]}{" "}
                <span className="num">
                  {req.oldValue} → {req.newValue}
                </span>{" "}
                <ApChange row={row} req={req} />{" "}
                <span className="num text-muted-foreground">
                  (RPN legacy {req.rpnBefore} → {req.rpnAfter})
                </span>
              </span>
            </div>
            <p className="mt-1 text-muted-foreground">
              Proposed by <span className="text-foreground">{req.proposedByLabel}</span> ·{" "}
              <span className="num">{fmtDate(req.createdAt)}</span> — “{req.rationale}”
            </p>
            <ul className="mt-1 space-y-0.5">
              {offs.map((o) => (
                <li
                  key={o.id}
                  className={o.step === "reject" ? "text-destructive" : "text-muted-foreground"}
                >
                  <span className="text-foreground">{STEP_LABEL[o.step]}</span>
                  {o.step === "reject" && o.roleBasis
                    ? ` (${SIGNOFF_ROLE_LABEL[o.roleBasis]})`
                    : ""}{" "}
                  · {o.decidedByLabel} · <span className="num">{fmtDate(o.createdAt)}</span>
                  {o.step !== "applied" && <> — “{o.comment}”</>}
                </li>
              ))}
            </ul>
            {status === "pending" && <SignoffSlots request={req} signoffs={signoffs} />}
            {status === "pending" && (
              <SignoffActions request={req} signoffs={signoffs} me={me} onSignOff={onSignOff} />
            )}
          </div>
        );
      })}
      {legacy.map((rev) => (
        <p key={rev.id} className="text-xs text-muted-foreground">
          <span className="rounded border border-border px-1 py-0.5 text-[0.6rem] uppercase tracking-wider">
            Made before dual sign-off was required
          </span>{" "}
          {PFMEA_FIELD_LABEL[rev.field]}{" "}
          <span className="num">
            {rev.oldValue} → {rev.newValue}
          </span>{" "}
          — “{rev.rationale}”{" "}
          <span className="num">
            · {rev.author} · {fmtDate(rev.at)}
          </span>
        </p>
      ))}
    </div>
  );
}

/** AP before → after for a change request. */
export function ApChange({
  row,
  req,
}: {
  row: PfmeaRow | undefined;
  req: Pick<PfmeaChangeRequest, "field" | "oldValue" | "newValue">;
}) {
  const ap = requestApChange(row, req);
  if (!ap) return null;
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      AP <ApBadge ap={ap.before} /> → <ApBadge ap={ap.after} />
    </span>
  );
}
